import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Space, Input, Select, Tag, Pagination } from "antd";
import {
  ExperimentOutlined,
  FileSearchOutlined,
  MedicineBoxOutlined,
  FileTextOutlined,
  SettingOutlined,
  FilePdfOutlined,
  InfoCircleFilled,
  DownloadOutlined,
  EditFilled,
  DeleteFilled,
  FileFilled,
} from "@ant-design/icons";
import { useGetPatientLabReportsQuery } from "../../utils/apiSlice";
import { useEffectiveUserData } from "../../hooks/useEffectiveUserData";
import "./MyVitalsReport.scss"
const { Option } = Select;

const Reports = () => {
  const [activeTab, setActiveTab] = useState("prescriptions");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const userData = useEffectiveUserData();
  const patientId = userData?.patientId || userData?.userId || "";

  const { data: labApiResponse, isLoading: isLabLoading } = useGetPatientLabReportsQuery(
    { patientId, documentCategory: "LabReport", page: 0, size: 10 },
    { skip: !patientId || activeTab !== "lab" }
  );
  const { data: imagingApiResponse, isLoading: isImagingLoading } = useGetPatientLabReportsQuery(
    { patientId, documentCategory: "Imaging", page: 0, size: 10 },
    { skip: !patientId || activeTab !== "imaging" }
  );
  const { data: prescriptionApiResponse, isLoading: isPrescriptionLoading } = useGetPatientLabReportsQuery(
    { patientId, documentCategory: "Prescription", page: 0, size: 10 },
    { skip: !patientId || activeTab !== "prescriptions" }
  );
  const { data: notesApiResponse, isLoading: isNotesLoading } = useGetPatientLabReportsQuery(
    { patientId, documentCategory: "DoctorNotes", page: 0, size: 10 },
    { skip: !patientId || activeTab !== "notes" }
  );



  /* ---------------- Table Columns ---------------- */

  const labColumns = [
    { title: "Patient", dataIndex: "patient" },
    { title: "Test Name", dataIndex: "test" },
    { title: "Doctor", dataIndex: "doctor" },
    {
      title: "Date & Time",
      dataIndex: "date",
      render: (text: any) => {
        const s = text != null && text !== "" ? String(text) : "-";
        if (s === "-") {
          return <span style={{ color: "#8c8c8c" }}>-</span>;
        }
        const parts = s.split(" ");
        return (
          <div style={{ fontSize: "12px" }}>
            <div style={{ fontWeight: "500" }}>{parts[0]}</div>
            <div style={{ color: "#8c8c8c" }}>{parts.slice(1).join(" ") || "—"}</div>
          </div>
        );
      },
    },
    {
      title: "Finding",
      dataIndex: "finding",
      render: (finding: any) => (
        <Tag
          style={{ borderRadius: "15px", padding: "0 15px", fontWeight: "500" }}
          color={finding === "Abnormal" ? "error" : "success"}
        >
          {finding}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: () => (
        <Space>
          <Button

            shape="circle"
            icon={<EditFilled />}
            className="btn-edit"

          />
          <Button
            shape="circle"
            icon={<DeleteFilled />}
            className="btn-delete"
          />
          <Button
            shape="circle"
            icon={<FileFilled />}
            className="small-icon"
          />
        </Space>
      ),
    },
  ];

  const labData = useMemo(() => {
    const items = labApiResponse?.data?.items || [];
    return items.map((item: any, index: number) => ({
      key: item.id || `lab-${index}`,
      patient: item.patientName || "-",
      test: item.documentSubtype || item.noteType || "Lab Report",
      doctor: item.doctorName || "-",
      date: item.startDate || item.prescriptionDate || "-",
      finding: item.documentSummary ? "Normal" : "Pending",
    }));
  }, [labApiResponse]);

  /* ---------------- IMAGING ---------------- */

  const imagingColumns = [
    { title: "Patient", dataIndex: "patient" },
    { title: "Imaging Type", dataIndex: "imaging" },
    { title: "Doctor", dataIndex: "doctor" },
    { title: "Date & Time", dataIndex: "date" },
    { title: "Finding", dataIndex: "finding" },
    {
      title: "Action",
      key: "action",
      render: () => (
        <Space>
          <Button

            shape="circle"
            icon={<EditFilled />}
            className="btn-edit"

          />
          <Button
            shape="circle"
            icon={<DeleteFilled />}
            className="btn-delete"
          />
          <Button
            shape="circle"
            icon={<FileFilled />}
            className="small-icon"
          />
        </Space>
      ),
    },
  ];

  const imagingData:any = useMemo(() => {
    const items = imagingApiResponse?.data?.items || [];
    return items.map((item: any, index: number) => ({
      key: item.id || `imaging-${index}`,
      patient: item.patientName || "-",
      imaging: item.documentSubtype || "Imaging",
      doctor: item.doctorName || "-",
      date: item.startDate || item.prescriptionDate || "-",
      finding: item.documentSummary ? "Normal" : "Pending",
    }));
  }, [imagingApiResponse]);

  /* ---------------- PRESCRIPTIONS ---------------- */

  const prescriptionColumns:any = [
    { title: "Patient", dataIndex: "patient" },
    { title: "Medicine Name", dataIndex: "medicine" },
    { title: "Doctor", dataIndex: "doctor" },
    { title: "Start Date", dataIndex: "startDate" },
    {
      title: "Action",
      key: "action",
      render: () => (
        <Space>
          <Button

            shape="circle"
            icon={<EditFilled />}
            className="btn-edit"

          />
          <Button
            shape="circle"
            icon={<DeleteFilled />}
            className="btn-delete"
          />
          <Button
            shape="circle"
            icon={<FileFilled />}
            className="small-icon"
          />
        </Space>
      ),
    },
  ];

  const prescriptionData:any = useMemo(() => {
    const items = prescriptionApiResponse?.data?.items || [];
    return items.map((item: any, index: number) => ({
      key: item.id || `prescription-${index}`,
      patient: item.patientName || "-",
      medicine: Array.isArray(item.medications) && item.medications.length
        ? item.medications.map((med: any) => med?.name).filter(Boolean).join(", ")
        : "-",
      doctor: item.doctorName || "-",
      startDate: item.startDate || item.prescriptionDate || "-",
    }));
  }, [prescriptionApiResponse]);

  /* ---------------- DOCTOR NOTES ---------------- */

  const notesColumns :any= [
    { title: "Patient", dataIndex: "patient" },
    { title: "Note Type", dataIndex: "type" },
    { title: "Doctor", dataIndex: "doctor" },
    { title: "Date & Time", dataIndex: "date" },
    { title: "Summary", dataIndex: "summary" },
    {
      title: "Action",
      key: "action",
      render: () => (
        <Space>
          <Button

            shape="circle"
            icon={<EditFilled />}
            className="btn-edit"

          />
          <Button
            shape="circle"
            icon={<DeleteFilled />}
            className="btn-delete"
          />
          <Button
            shape="circle"
            icon={<FileFilled />}
            className="small-icon"
          />
        </Space>
      ),
    },
  ];

  const notesData:any = useMemo(() => {
    const items = notesApiResponse?.data?.items || [];
    return items.map((item: any, index: number) => ({
      key: item.id || `notes-${index}`,
      patient: item.patientName || "-",
      type: item.noteType || "-",
      doctor: item.doctorName || "-",
      date: item.startDate || item.prescriptionDate || "-",
      summary: item.documentSummary || "-",
    }));
  }, [notesApiResponse]);

  /* ---------------- PROCEDURES ---------------- */

  const procedureColumns:any = [
    { title: "Patient", dataIndex: "patient" },
    { title: "Procedure", dataIndex: "procedure" },
    { title: "Doctor", dataIndex: "doctor" },
    { title: "Date & Time", dataIndex: "date" },
    { title: "Status", dataIndex: "status" },
    {
      title: "Action",
      key: "action",
      render: () => (
        <Space>
          <Button

            shape="circle"
            icon={<EditFilled />}
            className="btn-edit"

          />
          <Button
            shape="circle"
            icon={<DeleteFilled />}
            className="btn-delete"
          />
          <Button
            shape="circle"
            icon={<FileFilled />}
            className="small-icon"
          />
        </Space>
      ),
    },
  ];

  const procedureData:any = [
    {
      key: 1,
      patient: "James Bond",
      procedure: "Appendectomy",
      doctor: "Dr. Alex Green",
      date: "2026-02-20 03:00 PM",
      status: "Completed",
    },
    {
      key: 2,
      patient: "Lisa Smith",
      procedure: "Gallbladder Surgery",
      doctor: "Dr. Linda Lee",
      date: "2026-02-18 02:00 PM",
      status: "Scheduled",
    },
  ];

  // Must match default activeTab ("prescriptions") or column dataIndex/render won't align with rows.
  const [columns, setColumns] = useState(prescriptionColumns);
  const tabs = [
    { id: "prescriptions", label: "Prescriptions", icon: <MedicineBoxOutlined /> },
    { id: "notes", label: "Doctor Notes", icon: <FileTextOutlined /> },
    { id: "lab", label: "Lab Report", icon: <ExperimentOutlined /> },
    { id: "imaging", label: "Imaging", icon: <FileSearchOutlined /> },
    // { id: "procedures", label: "Procedures", icon: <SettingOutlined /> },
  ];
  const handleTabChange = (tabId:any) => {
  setActiveTab(tabId);

  if (tabId === "lab") {
    setColumns(labColumns);
  }

  if (tabId === "imaging") {
    setColumns(imagingColumns);
  }

  if (tabId === "prescriptions") {
    setColumns(prescriptionColumns);
  }

  if (tabId === "notes") {
    setColumns(notesColumns);
  }

  if (tabId === "procedures") {
    setColumns(procedureColumns);
  }
};
const rawData =
  activeTab === "lab"
    ? labData
    : activeTab === "imaging"
      ? imagingData
      : activeTab === "prescriptions"
        ? prescriptionData
        : activeTab === "notes"
          ? notesData
          : procedureData;

const data = useMemo(() => {
  if (!searchText.trim()) return rawData;
  const lower = searchText.toLowerCase();
  return rawData.filter((row: any) =>
    Object.values(row).some(
      (val) => val && typeof val === "string" && val.toLowerCase().includes(lower)
    )
  );
}, [rawData, searchText]);

const paginatedData = useMemo(() => {
  const start = (currentPage - 1) * pageSize;
  return data.slice(start, start + pageSize);
}, [data, currentPage, pageSize]);

useEffect(() => {
  setCurrentPage(1);
}, [searchText, activeTab]);

useEffect(() => {
  const maxPage = Math.max(1, Math.ceil(data.length / pageSize));
  if (currentPage > maxPage) {
    setCurrentPage(maxPage);
  }
}, [data.length, pageSize, currentPage]);

const isLoading =
  (activeTab === "lab" && isLabLoading) ||
  (activeTab === "imaging" && isImagingLoading) ||
  (activeTab === "prescriptions" && isPrescriptionLoading) ||
  (activeTab === "notes" && isNotesLoading);
  return (
    <div className="content-wrapper">
      <div className="container-full table-padding">
        {/* Page Header */}
        <div className="page-header">
          <div className="breadcrumb">
            <h3 className="page-title">Reports</h3>
            <span className="customize-breadcrumb">
              <span className="separator"> - </span>
              <span className="current">Medical Reports</span>
            </span>
          </div>
        </div>

        {/* Card */}
        <div className="family-card" style={{ paddingTop: '0px' }}>
          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              padding: "7px 20px",
              background: "#f0f2f5",
              borderBottom: "1px solid #e5e7eb",
              borderRadius: '12px 12px 0 0'
            }}
          >
           {tabs.map((tab) => (
  <button
    key={tab.id}
    onClick={() => handleTabChange(tab.id)}
    className={`custom-tab ${activeTab === tab.id ? "active" : ""}`}
  >
    {tab.icon} {tab.label}
  </button>
))}
          </div>

          {/* Header */}
          <div className="family-header" style={{ padding: "15px",  marginBottom:"0"}}>
            <div
              className="left-side"
              // style={{ display: "flex", gap: "15px", alignItems: "center" }}
            >
              <p className="title">Medical Reports</p>
              <Input.Search placeholder="Search..." style={{ width: 250 }} value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear />
              <Select placeholder="Month" style={{ width: 150 }}>
                <Option value="3">March</Option>
              </Select>
              <Select placeholder="Year" style={{ width: 120 }}>
                <Option value="2026">2026</Option>
              </Select>
            </div>
          </div>

          {/* Table */}
          
            <Table
              columns={columns}
              dataSource={paginatedData}
              loading={isLoading}
              pagination={false}
              rowClassName={(record, index) =>
                index % 2 === 0 ? "table-row-light" : ""
              }
              components={{
                header: {
                  cell: (props) => (
                    <th
                      {...props}
                      style={{
                        background: "#d6eaff",
                        color: "#1890ff",
                        fontWeight: "bold",
                      }}
                    />
                  ),
                },
              }}
            />
          
{/* 
           {!data.length && (
            <div style={{ padding: "40px", textAlign: "center", color: "#888" }}>
              No Data Available
            </div>
          )} */}

          {/* Footer */}
          <div className="family-footer">
            <span>Total {data.length} Reports</span>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={data.length}
              showSizeChanger
              pageSizeOptions={["5", "10", "20", "50"]}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              onShowSizeChange={(_, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;